<template>
  <sticky-header-layout>
    <template #header>
      <div class="action-container justify-content-end">
        <div class="button-container">
          <el-button
            :disabled="isLoading"
            icon="el-icon-close"
            size="mini"
            @click="handleCancel"
          >{{ $t('button.cancel') }}</el-button>

          <el-popover
            class="test-button-popover"
            placement="top-start"
            width="260"
            trigger="hover"
            content="Push to [goxiptest] immediate"
          >
            <el-button
              slot="reference"
              :disabled="isLoading"
              class="mx-2"
              size="mini"
              type="primary"
              @click="() => handleSave(true)"
            >
              <font-awesome-icon
                class="test-push-btn"
                icon="eye"
              />
              {{ 'Test Push' }}
            </el-button>
          </el-popover>
          <el-button
            :loading="isLoading"
            class="add-button"
            type="danger"
            icon="el-icon-edit"
            @click="() => handleSave(false)"
          >{{ $t('button.save') }}</el-button>
        </div>
      </div>
    </template>

    <div v-loading="isLoading">
      <generic-form
        ref="form"
        :attributes="formAttributes"
        :form="form"
        :form-template="formTemplate"
        class="pt-3"
      >
        <template
          v-for="(row, index) in formTemplate"
        >
          <template v-if="row.isSlot">
            <el-form-item
              :key="row.key"
              :label="$t(`table.${row.key}`)"
              :prop="row.key"
              :style="{ order: index }"
              :rules="row.rules"
            >
              <emoji-picker-input
                v-if="row.isEmojiPicker"
                :form="form"
                :type="row.key"
              />
            </el-form-item>
          </template>
        </template>
      </generic-form>
    </div>

  </sticky-header-layout>
</template>

<script>
import subDays from 'date-fns/subDays'
import endOfToday from 'date-fns/endOfToday'

import GenericForm from '../../components/form/Form.vue'
import EmojiPickerInput from './EmojiPickerInput.vue'

import NotificationService from '../../api/notifications'
import {
  pushNotificationTargetGroups,
  getDefaultPushNotificationDetail,
  pushNotificationFormTemplate,
} from '../../const/notifications'

export default {
  name: 'PushNotificationCreate',
  components: {
    EmojiPickerInput,
    GenericForm,
  },
  props: {
    id: {
      type: [String, Number],
      default: undefined,
    },
  },
  data() {
    return {
      form: getDefaultPushNotificationDetail(),
      formTemplate: pushNotificationFormTemplate,
      targetGroups: pushNotificationTargetGroups,
      dateTimePickerOptions: {
        disabledDate(time) {
          return time.getTime() <= subDays(endOfToday(), 1);
        },
      },
      checkAll: false,
      isIndeterminate: false,
      isLoading: false,
      isSaving: false,
      isEdited: false,
    }
  },
  computed: {
    isCustomPushTimeSelected() {
      return this.form.push_time_method === 'custom'
    },
    formAttributes() {
      return {
        disabled: this.isSaving,
        labelWidth: '132px',
      }
    },
    isEditMode() {
      return !!this.id
    },
  },
  watch: {
    form: {
      handler() {
        this.isEdited = true
      },
      deep: true,
    },
    'form.push_time_method': function _() {
      this.$refs.form.$refs.form.validateField('target_push_time')
    },
  },
  beforeRouteLeave(to, from, next) {
    if (this.isSaving) {
      return next(false)
    }
    if (!this.isEdited) {
      return next()
    }

    this.$confirm(this.$t('message.leaveWithUnsavedChanges'), this.$t('common.warning'), {
      confirmButtonText: this.$t('button.leave'),
      cancelButtonText: this.$t('button.cancel'),
      type: 'warning',
    }).then(() => next()).catch(() => next(false))

    return next(false)
  },
  created() {
    if (this.isEditMode) {
      this.getDetail(this.id)
    }
  },
  methods: {
    async getDetail(id) {
      this.isLoading = true
      try {
        const response = await NotificationService.getDetail({ id })
        this.form = response.data
        this.$nextTick(() => {
          this.isEdited = false
        })
        this.isLoading = false
      } catch (error) {
        this.isLoading = false
        this.$alert(this.$t('error.categoryNotFound'), this.$t('button.error'), {
          confirmButtonText: this.$t('button.ok'),
          type: 'error',
        }).then(() => {
          this.goToList()
        })
      }
    },
    async createPushNotification(isTest) {
      this.isSaving = true
      const payload = {
        ...this.form,
        is_test: Number(isTest),
      }

      try {
        await NotificationService.create(payload)
        this.$alert('Successfully created notification!', 'Yeah!', {
          confirmButtonText: 'Back to list',
          type: 'success',
          showClose: false,
        }).then(() => {
          this.handleSaveSuccess()
        })
      } catch (error) {
        this.$alert('Something went wrong! Please try again later.', 'Oops..', {
          confirmButtonText: 'OK',
        });
      } finally {
        this.isSaving = false
      }
    },
    handleSaveSuccess() {
      this.isEdited = false
      this.goToListPage()
    },
    handleCancel() {
      this.goToListPage()
    },
    goToListPage() {
      this.$router.push({ path: '/push-notification/list' })
    },
    handleSave(isTest) {
      this.$refs.form.$refs.form.validate().then(() => {
        this.createPushNotification(isTest)
      }).catch(() => {
        this.$alert(this.$t('error.pleaseFillInAndCheckErrors'), this.$t('common.oops'), {
          confirmButtonText: this.$t('button.ok'),
        });
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/view-base.scss";

.test-push-btn {
  margin-right: 2px;
  margin-top: -2px;
}
</style>
